import React, { useEffect, useState } from "react";
import cx from "classnames";
import { IconTypes } from "@foris/avocado-icons";
import { Menu } from "@foris/avocado-suite";
import { useFloatingMenu } from "@common/hooks/useFloatingMenu";
import FloatingMenu from "@common/components/floating-menu";
import styles from "./groupActionButton.module.scss";

interface GroupActionButtonProps {
  menuItems: Array<{
    label: string;
    icon?: IconTypes;
    disabled?: boolean;
    onClick: () => void;
  }>;
  buttonVariant?: "primary" | "secondary" | "ghost" | "danger";
  menuVariant?: "compact" | "default";
}

export const GroupActionButton: React.FC<GroupActionButtonProps> = ({
  menuItems,
  buttonVariant,
  menuVariant = "default",
}) => {
  const { position, triggerRef } = useFloatingMenu();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!triggerRef.current) return;

    // Walk up the DOM to find the nearest ancestor that clips its children
    // (overflow auto, scroll, or hidden). Using it as the IntersectionObserver
    // root ensures the button hides as soon as the row is clipped by that
    // container — not only when it leaves the viewport entirely.
    const getScrollableAncestor = (el: Element | null): Element | null => {
      let node = el?.parentElement ?? null;
      while (node) {
        const { overflow, overflowY, overflowX } = getComputedStyle(node);
        if (/(auto|scroll|hidden)/.test(overflow + overflowY + overflowX)) return node;
        node = node.parentElement;
      }
      return null;
    };

    const scrollRoot = getScrollableAncestor(triggerRef.current);

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.1,
        root: scrollRoot,
        rootMargin: "0px",
      },
    );

    observer.observe(triggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={triggerRef as any} className={styles.groupActionButton}>
      {isVisible && (
        <FloatingMenu
          targetRef={triggerRef}
          className={styles.groupActionButton_floatingMenu}
          position={position}
        >
          <Menu
            className={cx(
              styles.groupActionButton_menu,
              styles[`groupActionButton_menu__${menuVariant}`],
            )}
            items={menuItems}
            listOrigin="right"
            variant={buttonVariant}
            size="sm"
            closeOnClick
          />
        </FloatingMenu>
      )}
    </div>
  );
};
